import { API_BASE_URL, apiFetch } from './client.js'

function resolveIconUrl(icon) {
  if (!icon) return ''
  if (/^https?:\/\//i.test(icon)) return icon
  if (!icon.startsWith('/')) return icon
  if (!/^https?:\/\//i.test(API_BASE_URL)) return icon

  const apiUrl = new URL(API_BASE_URL)
  return `${apiUrl.protocol}//${apiUrl.host}${icon}`
}

function normalizeApplication(application) {
  if (!application || typeof application !== 'object') {
    return null
  }

  return {
    id: application.id,
    name: application.name || '',
    platform: normalizePlatformFromApi(application.platform),
    bundleId: application.bundle_id || application.bundleId || '',
    icon: resolveIconUrl(application.icon || ''),
    serviceAccount: application.service_account ?? application.serviceAccount ?? null,
    sharedKey: application.shared_key ?? application.sharedKey ?? null,
    forceUpdateEnabled: application.force_update_enabled ?? application.forceUpdateEnabled ?? false,
    validatorEnabled:
      application.validator_enabled ??
      application.validatorEnabled ??
      application.is_validated ??
      application.isValidated ??
      false,
    createdAt: application.created_at || application.createdAt || null,
    updatedAt: application.updated_at || application.updatedAt || null,
  }
}

function extractList(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.results)) return payload.results
  if (Array.isArray(payload?.data)) return payload.data
  return []
}

function extractItem(payload) {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    if (payload.item) return payload.item
    if (payload.data && !Array.isArray(payload.data)) return payload.data
  }
  return payload
}

function buildPayload(values) {
  const payload = new FormData()
  const platform = String(values.platform ?? '').trim().toLowerCase()
  if (platform !== 'ios' && platform !== 'android') {
    throw new TypeError('Platform must be iOS or Android')
  }
  payload.set('name', values.name.trim())
  payload.set('platform', platform)
  payload.set('bundle_id', values.bundleId.trim())
  if (values.iconFile instanceof File) {
    payload.set('icon', values.iconFile)
  }
  return payload
}

function normalizePlatformFromApi(platform) {
  if (platform === 'ios' || platform === 'android') return platform
  const n = Number(platform)
  if (n === 1) return 'android'
  if (n === 2) return 'ios'
  return ''
}

export async function listApplications() {
  const payload = await apiFetch('/apps')
  return extractList(payload).map(normalizeApplication).filter(Boolean)
}

export async function getApplication(id) {
  const payload = await apiFetch(`/apps/${encodeURIComponent(id)}`)
  return normalizeApplication(extractItem(payload))
}

export async function createApplication(values) {
  const payload = await apiFetch('/apps', {
    method: 'POST',
    body: buildPayload(values),
  })
  return normalizeApplication(extractItem(payload))
}

export async function updateApplication(id, values) {
  const payload = await apiFetch(`/apps/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: buildPayload(values),
  })
  return normalizeApplication(extractItem(payload))
}

export async function deleteApplication(id) {
  await apiFetch(`/apps/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

export async function activateValidator(applicationId, { service_account, shared_key }) {
  const body = { service_account }
  if (shared_key != null && shared_key !== '') {
    body.shared_key = shared_key
  }
  const payload = await apiFetch(
    `/apps/${encodeURIComponent(applicationId)}/validator/activate`,
    {
      method: 'POST',
      body,
    }
  )
  return normalizeApplication(extractItem(payload))
}

// --- Force updater / white versions ---

function normalizeWhiteVersion(row) {
  if (!row || typeof row !== 'object') return null
  return {
    id: row.id,
    bundleVersion: row.bundle_version ?? row.bundleVersion ?? '',
    rcId: row.rc_id ?? row.rcId ?? null,
    applicationId: row.application_id ?? row.applicationId ?? null,
    createdAt: row.created_at ?? row.createdAt ?? null,
  }
}

function normalizeConfigVersion(row) {
  if (!row || typeof row !== 'object') return null
  return {
    id: row.id ?? null,
    bundleVersion: row.bundle_version ?? row.bundleVersion ?? '',
    status: row.status ?? '',
    updatedAt: row.updated_at ?? row.updatedAt ?? null,
    config: row.config ?? null,
  }
}

export async function listWhiteVersions(applicationId) {
  const payload = await apiFetch(`/services/apps/${encodeURIComponent(applicationId)}/white-versions`)
  const list = Array.isArray(payload) ? payload : extractList(payload)
  return list.map(normalizeWhiteVersion).filter(Boolean)
}

export async function listConfigVersions(applicationId) {
  const payload = await apiFetch(`/services/apps/${encodeURIComponent(applicationId)}/config-versions`)
  const list = Array.isArray(payload) ? payload : extractList(payload)
  return list.map(normalizeConfigVersion).filter(Boolean)
}

export async function addWhiteVersion(applicationId, bundleVersion) {
  const payload = await apiFetch(
    `/services/apps/${encodeURIComponent(applicationId)}/white-versions`,
    {
      method: 'POST',
      body: { bundle_version: bundleVersion.trim() },
    }
  )
  return normalizeWhiteVersion(extractItem(payload))
}

export async function deleteWhiteVersion(applicationId, rcId) {
  await apiFetch(
    `/services/apps/${encodeURIComponent(applicationId)}/white-versions/${encodeURIComponent(rcId)}`,
    { method: 'DELETE' }
  )
}

export async function activateForceUpdate(applicationId) {
  const payload = await apiFetch(
    `/services/apps/${encodeURIComponent(applicationId)}/force-updater/activate`,
    { method: 'POST' }
  )
  return normalizeApplication(extractItem(payload))
}
